import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { usePlatform } from '../../lib/platform'
import {
  sortByUrgency, urgencyKey, summarise,
} from '../../lib/licence'
import { Shell } from '../../components/Shell'
import PortalFrame from '../../components/PortalFrame'
import { Spinner, initials } from '../../components/ui'
import LicenceList from '../../components/LicenceList'

/**
 * The church's dashboard of every song it has been endorsed to use, across
 * every writer who has endorsed one — not per-writer, per-song. A church that
 * only ever saw licences one writer at a time would have no way to notice
 * "three renewals land the same week" until they'd already landed.
 *
 * A licence still says nothing about *access* — a song can sit here declined
 * or expired and church_members / can_reach_song are what actually gate
 * whether anyone can open it. This is the paperwork view, not the gate.
 */

export default function Endorsements() {
  const { church } = useSession()
  const { device } = usePlatform()
  const nav = useNavigate()
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [rowErr, setRowErr] = useState({})
  const [pcoConnected, setPcoConnected] = useState(null)
  const [pushingId, setPushingId] = useState(null)
  const [events, setEvents] = useState({})
  // Open push failures for this church, keyed by song_id.
  const [failures, setFailures] = useState({})

  const load = useCallback(async () => {
    if (!church) return
    const { data, error } = await supabase
      .from('song_licences')
      .select('*, songs(title), songwriter:users!song_licences_songwriter_id_fkey(first_name, last_name, email)')
      .eq('church_id', church.id)
    if (error) setErr(readableError(error))
    setRows(data ?? [])

    // The same decision log the songwriter sees, from this side. Fetched here
    // because LicenceList deliberately queries nothing itself.
    const ids = (data ?? []).map((r) => r.id)
    if (ids.length === 0) return setEvents({})
    const { data: evs } = await supabase
      .from('song_licence_events')
      .select('id, licence_id, event, reason, occurred_at')
      .in('licence_id', ids)
      .order('occurred_at', { ascending: false })
    setEvents((evs ?? []).reduce((acc, e) => {
      ;(acc[e.licence_id] ||= []).push(e)
      return acc
    }, {}))
  }, [church])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!church) return
    supabase.rpc('church_pco_connected', { p_church_id: church.id })
      .then(({ data }) => setPcoConnected(!!data))
  }, [church])

  const setOneErr = (id, message) => setRowErr((prev) => ({ ...prev, [id]: message }))

  const push = async (row) => {
    setPushingId(row.id)
    setOneErr(row.id, null)
    const { data, error } = await supabase.functions
      .invoke('pco-push-song', { body: { song_id: row.song_id } })
    setPushingId(null)

    if (error) {
      // A push that failed at Planning Center comes back as a 502 with a
      // structured body — failure_class, whether it was queued, how many
      // attempts. supabase-js turns any non-2xx into an opaque error and hides
      // that body on error.context, so read it rather than showing the generic
      // "Edge Function returned a non-2xx status code", which tells an admin
      // nothing about whether they should wait, reconnect, or fix the song.
      let detail = null
      try { detail = await error.context?.json() } catch { /* not JSON — fall back */ }
      setOneErr(row.id, detail?.error ?? readableError(error))
      loadFailures()
      return
    }

    // A 200 can still be a partial: the song landed, the arrangement didn't.
    if (data?.partial) {
      setOneErr(row.id, 'The song is in the library, but its chord chart did not attach. Retry to finish it.')
    }
    load()
    loadFailures()
  }

  /**
   * The open push queue for this church. Read separately from the licence rows
   * because a failure outlives the click that caused it — an admin who closed
   * the tab mid-push should still find it waiting here.
   */
  const loadFailures = useCallback(async () => {
    if (!church) return
    const { data } = await supabase.from('pco_push_failures')
      .select('id, song_id, failure_class, failure_reason, attempts, last_attempt_at, next_retry_after')
      .eq('church_id', church.id).is('resolved_at', null)
      .order('last_attempt_at', { ascending: false })
    setFailures((data ?? []).reduce((acc, f) => { acc[f.song_id] = f; return acc }, {}))
  }, [church])

  /**
   * Closes a queue entry the admin dealt with by hand. The PCO song id is
   * optional but worth asking for: with it, the licence is marked pushed and
   * links to the real record, so no later retry can create a duplicate of the
   * song they just added themselves.
   */
  const resolveByHand = async (failure) => {
    const pcoId = window.prompt(
      'Marking this as added by hand.\n\n'
      + 'If you have the Planning Center song ID (the number in the song\'s URL), '
      + 'paste it here so Beseter can link to it and never push a duplicate. '
      + 'Leave blank to just clear it.',
      '',
    )
    // Cancel returns null; an empty string is a deliberate "clear it anyway".
    if (pcoId === null) return
    const { error } = await supabase.rpc('pco_push_resolve_manually', {
      p_failure_id: failure.id,
      p_pco_song_id: pcoId.trim() || null,
    })
    if (error) return setErr(readableError(error))
    load()
    loadFailures()
  }

  useEffect(() => { loadFailures() }, [loadFailures])

  const decline = async (row) => {
    if (!window.confirm('Decline this licence? This cannot be undone — the songwriter would need to propose it again.')) {
      return
    }
    setBusyId(row.id)
    setOneErr(row.id, null)
    const { error } = await supabase.from('song_licences')
      .update({ status: 'declined' }).eq('id', row.id)
    setBusyId(null)
    if (error) return setOneErr(row.id, readableError(error))
    load()
  }

  const isPhone = device === 'phone'
  if (!church || rows === null) {
    return isPhone
      ? <Shell back="/admin/people" title="People"><Spinner /></Shell>
      : <PortalFrame title="Endorsements"><Spinner /></PortalFrame>
  }

  const writerName = (r) =>
    [r.songwriter?.first_name, r.songwriter?.last_name].filter(Boolean).join(' ')
    || r.songwriter?.email || '—'

  const shaped = sortByUrgency(rows).map((r) => ({
    ...r,
    song_title: r.songs?.title,
    songwriter_name: writerName(r),
  }))

  const pending = shaped.filter((r) => r.status === 'proposed')
  const upcoming = shaped.filter((r) => {
    const [group] = urgencyKey(r)
    return r.status !== 'proposed' && group <= 1
  })
  const settled = shaped.filter((r) => {
    const [group] = urgencyKey(r)
    return group > 1
  })

  const body = (
    <>
      <div className="pf-card">
        <div className="pf-cardhead">
          <span className="pf-cardtitle">Waiting on your decision</span>
          <span className="pf-count">{pending.length}</span>
        </div>
        {pending.length === 0 && <p className="pf-empty">Nothing proposed right now.</p>}
        {pending.map((row) => {
          const isOpen = openId === row.id
          const busy = busyId === row.id
          return (
            <div key={row.id} className="cosign" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <div className="ava" aria-hidden="true">{initials(row.songwriter_name || row.song_title)}</div>
              <button
                type="button"
                className="who"
                style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                onClick={() => setOpenId(isOpen ? null : row.id)}
                aria-expanded={isOpen}
              >
                <div className="n">{row.song_title || 'Untitled'}</div>
                <div className="st">{row.songwriter_name || '—'} · {isOpen ? 'hide full terms' : 'read full terms'}</div>
              </button>

              <span className="pf-rowbtns">
                <button className="pf-btn pf-btn-solid" disabled={busy}
                        onClick={() => nav(`/admin/endorsements/${row.id}/review/1`)}>
                  Review &amp; decide
                </button>
                <button className="pf-btn pf-btn-quiet" disabled={busy} onClick={() => decline(row)}>
                  Decline
                </button>
              </span>

              {isOpen && (
                <div style={{ flexBasis: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                  {summarise(row).map((line, i) => (
                    <p key={i} className="small" style={{ margin: '3px 0' }}>{line}</p>
                  ))}
                </div>
              )}
              {rowErr[row.id] && (
                <p className="err" style={{ flexBasis: '100%', marginTop: 8 }}>{rowErr[row.id]}</p>
              )}
            </div>
          )
        })}
      </div>

      <div className="pf-card" style={{ marginTop: 14 }}>
        <div className="pf-cardhead">
          <span className="pf-cardtitle">Renewing soon</span>
          <span className="pf-count">{upcoming.length}</span>
        </div>
        <LicenceList rows={upcoming} perspective="church"
                     emptyLabel="Nothing coming up for renewal."
                     onPush={push} pushingId={pushingId} pcoConnected={pcoConnected} rowErr={rowErr}
                     failures={failures} onResolveByHand={resolveByHand}
                     events={events} />
      </div>

      <div className="pf-card" style={{ marginTop: 14 }}>
        <div className="pf-cardhead">
          <span className="pf-cardtitle">Everything else</span>
          <span className="pf-count">{settled.length}</span>
        </div>
        <LicenceList rows={settled} perspective="church"
                     emptyLabel="No other endorsed songs yet."
                     onPush={push} pushingId={pushingId} pcoConnected={pcoConnected} rowErr={rowErr}
                     failures={failures} onResolveByHand={resolveByHand}
                     events={events} />
      </div>
    </>
  )

  if (!isPhone) {
    return (
      <PortalFrame title="Endorsements"
                   sub="Every song endorsed to this church, and when each licence is next up for renewal.">
        {body}
        {err && <p className="err">{err}</p>}
      </PortalFrame>
    )
  }

  return (
    <Shell back="/admin/people" title="People">
      <div className="eyebrow">Administrator</div>
      <h2 className="q">Endorsements</h2>
      <p className="sub">
        Every song endorsed to {church.name}, and when each licence is next up for renewal.
      </p>
      {body}
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
